import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useHistory } from 'react-router-dom';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  BreadcrumbContainer,
  BreadcrumbLinkComponent,
  PageHeader,
} from 'app/components/StyledComponents';
import { Breadcrumbs } from '@mui/material';
import LoadingIndicator from 'app/components/LoadingIndicator';
import { formatDateForDisplay } from 'utils/dateUtils';
import { titleCase } from 'utils/text';
import { UnsoldLeadDetailsProvider, useUnsoldLeadDetails } from './context/UnsoldLeadDetailsContext';
import { useLeadIntelligence } from './hooks/useLeadIntelligence';
import { useNurturingStrategy } from './hooks/useNurturingStrategy';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  height: '100%',
  width: '100%',
  padding: theme.spacing(3),
}));

const InfoSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(0.5),
}));

const ValueText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const IntelligenceBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#e8f5e9',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const StrategyBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#e3f2fd',
  borderRadius: theme.spacing(1),
}));

const IntelligenceTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: '#2e7d32',
  marginBottom: theme.spacing(2),
}));

const StrategyTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: '#1976d2',
  marginBottom: theme.spacing(2),
}));

const UnsoldLeadDetailsContent = () => {
  const { leadDetails, loading, error } = useUnsoldLeadDetails();
  const leadIntelligence = useLeadIntelligence();
  const nurturingStrategy = useNurturingStrategy();
  const history = useHistory();

  // Get the metric name from URL state or localStorage to show in breadcrumb
  const [nurturingMetricName, setNurturingMetricName] = React.useState<string>('Nurturing Prospects');

  React.useEffect(() => {
    // Try to get the metric name from location state (if navigated from UnsoldPage)
    const state = history.location.state as any;
    if (state?.metricName) {
      setNurturingMetricName(state.metricName);
    } else {
      // Fallback: check if we can get it from sessionStorage
      const storedMetricName = sessionStorage.getItem('unsoldSelectedMetric');
      if (storedMetricName) {
        setNurturingMetricName(storedMetricName);
      }
    }
  }, [history.location.state]);

  // Capitalize lead name for display
  const capitalizedLeadName = leadDetails?.name ? titleCase(leadDetails.name) : 'Lead Details';

  const breadcrumbsList = [
    {
      label: 'Unsold',
      isActive: false,
      link: '/ai-avatar-layout/unsold',
    },
    {
      label: nurturingMetricName,
      isActive: false,
      link: '/ai-avatar-layout/unsold',
      metricName: nurturingMetricName,
    },
    {
      label: capitalizedLeadName,
      isActive: true,
    },
  ];

  const handleClickBreadcrumbs = (breadcrumb: any, index: number) => {
    try {
      if (breadcrumb.isActive) {
        return;
      }
      if (breadcrumb.label === 'Unsold') {
        // Navigate to UnsoldPage and reset state
        sessionStorage.removeItem('unsoldSelectedMetric');
        history.push('/ai-avatar-layout/unsold');
      } else if (breadcrumb.metricName || breadcrumb.label === 'Nurturing Prospects') {
        // Navigate to UnsoldPage with data grid visible
        // Store the metric name so UnsoldPage can restore the state
        const metricName = breadcrumb.metricName || breadcrumb.label || 'Nurturing Prospects';
        sessionStorage.setItem('unsoldSelectedMetric', metricName);
        history.push({
          pathname: '/ai-avatar-layout/unsold',
          state: { 
            showDataGrid: true,
            metricName: metricName,
            // Pass the actual metric type enum value for better matching
            metricType: 'OPPORTUNITY_ADDED_IN_NURTURING',
          },
        });
      }
    } catch (error) {
      console.error('Error handling breadcrumb click:', error);
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'error.light', borderRadius: 1 }}>
        {error}
      </Box>
    );
  }

  if (!leadDetails) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No lead details found.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Unsold Lead: {capitalizedLeadName}</title>
        <meta name="description" content={`Unsold Lead: ${capitalizedLeadName} - Myautoiq`} />
      </Helmet>
      <Box
        sx={{
          height: 'calc(100vh - 66px)',
          padding: '0 48px',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '1000px',
        }}
      >
        <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <BreadcrumbContainer>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                {breadcrumbsList?.map((breadcrumb, index) => (
                  <BreadcrumbLinkComponent
                    key={`${breadcrumb.label}-${breadcrumb.isActive}-${index}`}
                    underline={breadcrumb.isActive ? 'none' : 'always'}
                    color={breadcrumb.isActive ? 'text.primary' : 'inherit'}
                    onClick={() => handleClickBreadcrumbs(breadcrumb, index)}
                    sx={{
                      cursor: breadcrumb.isActive ? 'default' : 'pointer',
                      color: breadcrumb.isActive ? 'text.primary' : 'inherit',
                    }}
                  >
                    {breadcrumb.label}
                  </BreadcrumbLinkComponent>
                ))}
              </Breadcrumbs>
            </BreadcrumbContainer>
            <PageHeader sx={{ marginBottom: '0 !important' }}>
              Unsold Lead: {capitalizedLeadName}
            </PageHeader>
          </Box>
        </Box>

        <MainContainer>
          {/* Lead Information Section */}
          <InfoSection>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <InfoRow>
                  <LabelText variant="body1">Email:</LabelText>
                  <ValueText variant="body1">{leadDetails.email}</ValueText>
                </InfoRow>
                <InfoRow>
                  <LabelText variant="body1">Phone:</LabelText>
                  <ValueText variant="body1">{leadDetails.phone}</ValueText>
                </InfoRow>
                <InfoRow>
                  <LabelText variant="body1">Lead In:</LabelText>
                  <ValueText variant="body1">
                    {formatDateForDisplay(leadDetails.lead_created_date, 'MMMM dd, yyyy') || leadDetails.lead_created_date}
                  </ValueText>
                </InfoRow>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoRow>
                  <LabelText variant="body1">Status:</LabelText>
                  <ValueText variant="body1">{leadDetails.status}</ValueText>
                </InfoRow>
                <InfoRow>
                  <LabelText variant="body1">Source:</LabelText>
                  <ValueText variant="body1">{leadDetails.source}</ValueText>
                </InfoRow>
                <InfoRow>
                  <LabelText variant="body1">Interested Vehicle:</LabelText>
                  <ValueText variant="body1">{leadDetails.voi_name}</ValueText>
                </InfoRow>
              </Grid>
            </Grid>
          </InfoSection>

          {/* Lead Intelligence Section */}
          <IntelligenceBox>
            <IntelligenceTitle variant="h6">Lead Intelligence:</IntelligenceTitle>
            {leadIntelligence.conversationId || leadIntelligence.loading || leadIntelligence.isStreaming || leadIntelligence.streamingText ? (
              leadIntelligence.streamingText || leadIntelligence.isStreaming ? (
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    fontSize: '18px',
                    color: 'text.primary',
                  }}
                >
                  <MarkupMAIQ content={leadIntelligence.streamingText || ''} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#2e7d32',
                      animation: 'chatGptThinking 1.4s ease-in-out infinite',
                      '@keyframes chatGptThinking': {
                        '0%': {
                          transform: 'scale(0.8)',
                          opacity: 0.5,
                        },
                        '50%': {
                          transform: 'scale(1.2)',
                          opacity: 1,
                        },
                        '100%': {
                          transform: 'scale(0.8)',
                          opacity: 0.5,
                        },
                      },
                    }}
                  />
                </Box>
              )
            ) : (
              <Typography variant="body1" color="text.primary">
                {leadDetails.lead_intelligence}
              </Typography>
            )}
          </IntelligenceBox>

          {/* Suggested Nurturing Strategy Section */}
          <StrategyBox>
            <StrategyTitle variant="h6">Suggested Nurturing Strategy:</StrategyTitle>
            {nurturingStrategy.conversationId || nurturingStrategy.loading || nurturingStrategy.isStreaming || nurturingStrategy.streamingText ? (
              nurturingStrategy.streamingText || nurturingStrategy.isStreaming ? (
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    fontSize: '18px',
                    color: 'text.primary',
                  }}
                >
                  <MarkupMAIQ content={nurturingStrategy.streamingText || ''} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#1976d2',
                      animation: 'chatGptThinking 1.4s ease-in-out infinite',
                      '@keyframes chatGptThinking': {
                        '0%': {
                          transform: 'scale(0.8)',
                          opacity: 0.5,
                        },
                        '50%': {
                          transform: 'scale(1.2)',
                          opacity: 1,
                        },
                        '100%': {
                          transform: 'scale(0.8)',
                          opacity: 0.5,
                        },
                      },
                    }}
                  />
                </Box>
              )
            ) : (
              <Box
                sx={{
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                  fontSize: '18px',
                  color: 'text.primary',
                }}
              >
                {leadDetails.suggested_strategy}
              </Box>
            )}
          </StrategyBox>
        </MainContainer>
      </Box>
    </>
  );
};

const UnsoldLeadDetailsPage = () => {
  const { leadId } = useParams<{ leadId: string }>();

  if (!leadId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Invalid lead ID.</Typography>
      </Box>
    );
  }

  return (
    <UnsoldLeadDetailsProvider leadId={leadId}>
      <Suspense fallback={<LoadingIndicator />}>
        <UnsoldLeadDetailsContent />
      </Suspense>
    </UnsoldLeadDetailsProvider>
  );
};

export default UnsoldLeadDetailsPage;

